<template>
  <div :class="[$tvaMq, route.params.page]" :version="version.version">
    <!-- Metatags -->
    <teleport to="head" v-if="route.params.page">
      <title>{{ $tm("metaContent")[route.params.page].title }}</title>
      <meta
        name="description"
        :content="$tm('metaContent')[route.params.page].description"
      />
      <!-- FACEBOOK -->
      <meta
        property="og:title"
        :content="$tm('metaContent')[route.params.page].title"
      />
      <meta
        property="og:description"
        :content="$tm('metaContent')[route.params.page].description"
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        :content="'https://www.eni.com/static/it-IT/infografiche/transizione-giusta/social/eni-just-transition.png'"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <!-- TWITTER -->
      <meta
        name="twitter:site"
        :content="$tm('metaContent')[route.params.page].title"
      />
      <meta
        name="twitter:title"
        :content="$tm('metaContent')[route.params.page].title"
      />
      <meta
        name="twitter:description"
        :content="$tm('metaContent')[route.params.page].description"
      />
      <meta
        name="twitter:image"
        :content="$tm('metaContent')[route.params.page].image"
      />
    </teleport>
    <!-- Routes -->
    <!-- <Loader v-if="!firstPageTransitionHappened" /> -->

    <router-view v-slot="{ Component, route }">
      <!-- <transition
        :name="smallscreen ? null : animationDirection"
        @after-enter="pageToTop"
      > -->
      <transition name="fade">
        <component :key="route.params.page" :is="Component" />
      </transition>
      <!-- </transition> -->
    </router-view>

    <!--     <transition name="fade">
      <NavBarDesktop v-if="!smallscreen &&
        firstPageTransitionHappened &&
        route.name !== 'road2050'
        " :pageLinks="pageLinks" :currentPage="currentPage" />
    </transition> -->

    <NavBarMobile
      :pageLinks="pageLinks"
      :caseStudyLinks="caseStudyLinks"
      :scrollUp="scrollUp"
    />

    <GeneralFooter />
  </div>
</template>

<script setup>
import version from "@/../package.json";
import { onMounted, provide, watch, computed, ref } from "@vue/runtime-core";
import useTvaMq from "./plugins/tvaMq.js";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import debounce from "lodash/debounce";

import GeneralFooter from "./components/atoms/GeneralFooter.vue";
import NavBarMobile from "./components/atoms/NavBarMobile.vue";
// import NavBarDesktop from "./components/general/NavBarDesktop.vue";
import Loader from "./components/general/Loader.vue";

const { $tvaMq } = useTvaMq();
const smallscreen = computed(() => ["tablet", "mobile"].includes($tvaMq.value));

const route = useRoute();
const router = useRouter();
const i18n = useI18n();

provide("$tvaMq", $tvaMq);
provide("version", version);

const currentPage = ref(null);
const firstPageTransitionHappened = ref(false);
const animationDirection = ref("fade");
const pages = computed(() => i18n.tm("pages"));
const caseStudies = computed(() => i18n.tm("case-study"));
const pageLinks = computed(() => Object.keys(pages.value));
const caseStudyLinks = computed(() => Object.keys(caseStudies.value));
const scrollUp = ref(false);

// Calling this here is equivalent to calling it in
// beforeCreated / create in Options API
setLanguage();

onMounted(() => {
  setVHProperty();

  window.addEventListener("resize", debounce(setStyleProperties));
  window.addEventListener("orientationchange", setStyleProperties);
});

watch(
  () => route.params.page,
  (current, prev) => {
    if (!prev) {
      animationDirection.value = "fade";
    } else {
      animationDirection.value =
        pageLinks.value.indexOf(current) > pageLinks.value.indexOf(prev)
          ? `page-slide-left`
          : `page-slide-right`;
    }

    setCurrentPage();
  }
);

let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;

window.addEventListener("scroll", () => {
  const scrollTopPosition =
    window.pageYOffset || document.documentElement.scrollTop;

  if (scrollTopPosition > lastScrollTop || scrollTopPosition == 0) {
    scrollUp.value = false;
    // console.log('scrolling down');
  } else if (scrollTopPosition < lastScrollTop) {
    scrollUp.value = true;
    // console.log('scrolling up');
  }
  lastScrollTop = scrollTopPosition <= 0 ? 0 : scrollTopPosition;
});

function pageToTop() {
  // This happens only once and is to prevent showing the desktop navbar too early
  if (!firstPageTransitionHappened.value) {
    firstPageTransitionHappened.value = true;
  }
  window.scrollTo(0, 0);
  setWidthMinusScrollbar();
}

function setCurrentPage() {
  if (route.name != "test" && !pageLinks.value.includes(route.params.page)) {
    router.push({
      name: "page",
      params: {
        page: "home",
      },
    });
  } else {
    currentPage.value = route.params.page;
  }
}

function setLanguage() {
  const defaultLang = "it";
  let lang;

  let langFromOutside =
    process.env.VUE_APP_LANG ||
    document.querySelector("#app").getAttribute("lang");

  if (langFromOutside) {
    lang = langFromOutside;
  } else {
    lang = defaultLang;
  }

  i18n.locale.value = lang.toLowerCase();

  // Set html language attribute
  const htmlEl = document.querySelector("html");
  if (htmlEl) {
    htmlEl.setAttribute("lang", lang.toLowerCase());
    window.digitalData[0].page.pageLanguage =
      lang.toLowerCase() == "it" ? "it-IT" : "en-IT";
    window.digitalData[0].page.pageUrl =
      lang.toLowerCase() == "it"
        ? "https://www.eni.com/static/it-IT/infografiche/transizione-giusta/"
        : "https://www.eni.com/static/en-IT/infographics/just-transition/";
  }
}

function setVHProperty() {
  if ($tvaMq.value === "mobile") {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--app-vh", `${vh}px`);
  }
}

function setWidthMinusScrollbar() {
  document.documentElement.style.setProperty(
    "--vw-no-scrollbar",
    document.documentElement.clientWidth + "px"
  );
}

function setStyleProperties() {
  setWidthMinusScrollbar();
  setVHProperty();
}
</script>

<style lang="scss">
#app {
  --vh: var(--app-vh, 1vh);

  @include p-m;

  font-family: "Open Sans", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  line-height: normal;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  blockquote {
    text-wrap: balance;
  }

  strong {
    font-weight: 700;
  }
}
</style>
